
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";

const BRAND = "#1d275f";

const sheetVariants = {
  hidden: { y: "100%", opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 280, damping: 28 },
  },
  exit: { y: "100%", opacity: 0, transition: { duration: 0.18 } },
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { type: "spring", stiffness: 300, damping: 30 } },
  exit: { opacity: 0, scale: 0.95, transition: { duration: 0.15 } },
};

function useDebounce(value, delay = 200) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function sanitizeCityInput(raw) {
  if (!raw) return "";
  const t = String(raw).trim();
  const cleaned = t.replace(/[^A-Za-z0-9 .,\-\/()]/g, "").replace(/\s+/g, " ");
  return cleaned
    .split(" ")
    .map((w) => (w.length > 1 ? w[0].toUpperCase() + w.slice(1) : w.toUpperCase()))
    .join(" ");
}

function extractCityFromFeature(feature) {
  if (!feature) return null;
  if (feature.place_type && feature.place_type.includes("place")) return feature.text;
  const ctxPlace = (feature.context || []).find((c) => c.id && c.id.startsWith("place."));
  if (ctxPlace && ctxPlace.text) return ctxPlace.text;
  const ctxDistrict = (feature.context || []).find(
    (c) => c.id && (c.id.startsWith("district.") || c.id.startsWith("region."))
  );
  if (ctxDistrict && ctxDistrict.text) return ctxDistrict.text;
  return feature.text || null;
}
// Country palette for <img> error fallback
const COUNTRY_PALETTE = {
  Germany: "#1d275f",  Netherlands: "#B22222", Belgium: "#2d2d2d",
  France:  "#0055A4",  Spain:       "#c60b1e", Italy:    "#009246",
  Austria: "#ED2939",  Switzerland: "#D52B1E", "Czech Republic": "#D7141A",
  Poland:  "#DC143C",  Denmark:     "#C60C30", Sweden:   "#006AA7",
  Norway:  "#EF3340",  Portugal:    "#006600", UK:       "#012169",
  Ireland: "#169B62",  Hungary:     "#CE2939", Romania:  "#002B7F",
  Greece:  "#0D5EAF",
};

const FLAG_MAP = {
  Germany: "🇩🇪", Netherlands: "🇳🇱", Belgium: "🇧🇪", France: "🇫🇷",
  Austria: "🇦🇹", Switzerland: "🇨🇭", UK: "🇬🇧", Spain: "🇪🇸",
  Italy: "🇮🇹", "Czech Republic": "🇨🇿", Poland: "🇵🇱", Denmark: "🇩🇰",
  Sweden: "🇸🇪", Norway: "🇳🇴", Portugal: "🇵🇹", Ireland: "🇮🇪",
  Hungary: "🇭🇺", Romania: "🇷🇴", Greece: "🇬🇷",
};

function getFlagEmoji(country) { return FLAG_MAP[country] || "🌍"; }

// CityCard — real photo with gradient fallback on error
function CityCard({ loc, isSelected, onClick, compact = false }) {
  const [imgFailed, setImgFailed] = React.useState(false);
  const imgH = compact ? 72 : 100;
  const bgColor = COUNTRY_PALETTE[loc.country] || "#2d3748";
  const flag = getFlagEmoji(loc.country);

  return (
    <button
      onClick={onClick}
      className="group relative rounded-xl overflow-hidden text-left focus:outline-none focus:ring-2 focus:ring-indigo-400 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5"
      style={{
        border: isSelected ? "2px solid #DC3558" : "1px solid #e5e7eb",
        background: "#fff",
      }}
    >
      {/* Image tile */}
      <div className="relative w-full overflow-hidden" style={{ height: imgH }}>
        {loc.img && !imgFailed ? (
          <img
            src={loc.img}
            alt={loc.name}
            onError={() => setImgFailed(true)}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
          />
        ) : (
          /* Gradient fallback when no img or img fails */
          <div
            className="w-full h-full flex items-center justify-center text-3xl"
            style={{ background: `linear-gradient(135deg, ${bgColor}cc, ${bgColor}66)` }}
          >
            {flag}
          </div>
        )}
        {/* Flag badge overlay (always shown on top of image) */}
        {loc.img && !imgFailed && (
          <span
            className="absolute top-1 right-1 text-sm leading-none drop-shadow"
            style={{ fontSize: compact ? 12 : 15 }}
          >
            {flag}
          </span>
        )}
        {/* Selected check badge */}
        {isSelected && (
          <span className="absolute top-1 left-1 bg-[#DC3558] text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full">
            ✓
          </span>
        )}
        {/* Gradient scrim so text is readable */}
        {loc.img && !imgFailed && (
          <div className="absolute inset-x-0 bottom-0 h-8" style={{ background: "linear-gradient(transparent, rgba(0,0,0,0.35))" }} />
        )}
      </div>

      {/* City name */}
      <div
        className="px-2 py-1.5 text-center"
        style={{ background: "#fff", minHeight: compact ? 28 : 36 }}
      >
        <span
          className="font-semibold leading-tight truncate block"
          style={{ fontSize: compact ? 10 : 12, color: "#1f2937" }}
        >
          {loc.name}
        </span>
      </div>
    </button>
  );
}

export default function CitySelectorModal({
  locations = [],
  handleCloseModal,
  locationLabel = null,
  isOpen = false,
}) {
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 180);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : true
  );

  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const inputRef = useRef(null);

  const [recent, setRecent] = useState(() => {
    try {
      const raw = localStorage.getItem("city_recent") || "[]";
      return JSON.parse(raw).slice(0, 5);
    } catch {
      return [];
    }
  });

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      setSuggestions([]);
      setSuggestionsOpen(false);
      setHighlightIndex(-1);
    } else {
      setTimeout(() => inputRef.current?.focus?.(), 80);
    }
  }, [isOpen]);

  // local grid filter
  const filtered = useMemo(() => {
    const q = (debouncedQuery || "").trim().toLowerCase();
    return q
      ? locations.filter((l) => (l.name || "").toLowerCase().includes(q))
      : locations.slice();
  }, [locations, debouncedQuery]);

  // Mapbox Places autocomplete
  useEffect(() => {
    let mounted = true;
    const token = process.env.REACT_APP_MAPBOX_KEY;

    async function fetchSuggestions(q) {
      if (!token || !q || q.length < 2) {
        if (mounted) { setSuggestions([]); setLoadingSuggestions(false); }
        return;
      }
      setLoadingSuggestions(true);
      const encoded = encodeURIComponent(q);
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encoded}.json?autocomplete=true&types=place,locality&limit=6&access_token=${token}&language=en`;
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error("Places fetch failed");
        const data = await res.json();
        if (!mounted) return;
        const s = (data.features || []).map((f) => ({
          id: f.id,
          display: extractCityFromFeature(f) || f.text,
          place_name: f.place_name,
          center: f.center,
          raw: f,
        }));
        setSuggestions(s);
        setSuggestionsOpen(s.length > 0);
        setHighlightIndex(0);
      } catch (err) {
        console.error("Mapbox Places error", err);
        if (mounted) { setSuggestions([]); setSuggestionsOpen(false); }
      } finally {
        if (mounted) setLoadingSuggestions(false);
      }
    }

    fetchSuggestions(debouncedQuery);
    return () => { mounted = false; };
  }, [debouncedQuery]);

  useEffect(() => {
    if (debouncedQuery && suggestions.length) {
      setSuggestionsOpen(true);
      setHighlightIndex(0);
    } else {
      setSuggestionsOpen(false);
      setHighlightIndex(-1);
    }
  }, [debouncedQuery, suggestions.length]);

  const pushRecent = (v) => {
    try {
      const arr = [v, ...recent.filter((r) => r !== v)].slice(0, 5);
      setRecent(arr);
      localStorage.setItem("city_recent", JSON.stringify(arr));
    } catch { /* ignore */ }
  };

  function selectSuggestion(s, meta = {}) {
    const value =
      typeof s === "string"
        ? s
        : s.display || s.place_name || (s.raw && s.raw.text);
    const sanitized = sanitizeCityInput(value);
    pushRecent(sanitized);
    handleCloseModal(sanitized, meta);
  }

  const onInputKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!suggestionsOpen && suggestions.length) setSuggestionsOpen(true);
      setHighlightIndex((i) => Math.min(i + 1, suggestions.length - 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (suggestionsOpen && highlightIndex >= 0 && suggestions[highlightIndex]) {
        selectSuggestion(suggestions[highlightIndex], { custom: false, source: "mapbox" });
      } else if (filtered.length === 1) {
        selectSuggestion(filtered[0].value || filtered[0].name, { custom: false, source: "local" });
      } else {
        const sanitized = sanitizeCityInput(query);
        if ((sanitized || "").length >= 2) {
          selectSuggestion(sanitized, { custom: true });
        } else {
          toast.error("Please enter a valid city name");
        }
      }
      setSuggestionsOpen(false);
    }
    if (e.key === "Escape") {
      handleCloseModal("All");
    }
  };

  const detectLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    navigator.geolocation.getCurrentPosition(
      async (p) => {
        const { latitude, longitude } = p.coords;
        const token = process.env.REACT_APP_MAPBOX_KEY;
        if (!token) { toast.error("Location detection requires Mapbox key"); return; }
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?types=place,locality&limit=1&access_token=${token}&language=en`;
        try {
          const r = await fetch(url);
          const data = await r.json();
          const feat = data.features && data.features[0];
          const city = extractCityFromFeature(feat);
          if (city) {
            const sanitized = sanitizeCityInput(city);
            pushRecent(sanitized);
            handleCloseModal(sanitized, { custom: false, source: "mapbox-reverse" });
            toast.success(`Detected: ${city}`);
          } else {
            toast.info("Location detected but no city found");
          }
        } catch (err) {
          console.error(err);
          toast.error("Unable to detect location");
        }
      },
      () => toast.error("Permission denied or location unavailable"),
      { enableHighAccuracy: true, timeout: 7000 }
    );
  };

  const dismissAsAll = () => handleCloseModal("All");

  const renderSuggestionLabel = (s) => {
    const q = (debouncedQuery || "").trim();
    const label = s.display || s.place_name || "";
    if (!q) return label;
    const idx = label.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return label;
    return (
      <>
        {label.slice(0, idx)}
        <strong className="text-indigo-700">{label.slice(idx, idx + q.length)}</strong>
        {label.slice(idx + q.length)}
      </>
    );
  };

  // ── Skeleton rows ──
  const SkeletonRows = () => (
    <>
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-3 animate-pulse">
          <div className="w-8 h-8 rounded-full bg-gray-200 shrink-0" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 bg-gray-200 rounded" style={{ width: `${50 + i * 10}%` }} />
            <div className="h-2 bg-gray-100 rounded" style={{ width: "40%" }} />
          </div>
        </div>
      ))}
    </>
  );

  // ── City Card (shared between desktop and mobile) ──
  const CityCard = ({ loc, isSelected, onClick, compact = false }) => (
    <button
      onClick={onClick}
      className="group relative rounded-xl overflow-hidden text-left focus:outline-none focus:ring-2 focus:ring-indigo-400 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5"
      style={{
        border: isSelected ? "2px solid #DC3558" : "1px solid #e5e7eb",
        background: "#fff",
      }}
    >
      {/* Image container — fixed height so it doesn't swallow the name */}
      <div
        className="w-full overflow-hidden bg-gray-100 relative"
        style={{ height: compact ? 56 : 80 }}
      >
        {loc.img ? (
          <img
            src={loc.img}
            alt={loc.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center text-2xl"
            style={{ background: "linear-gradient(135deg, #f3f4f6, #e5e7eb)" }}
          >
            {getFlagEmoji(loc.country)}
          </div>
        )}
        {/* Selected badge */}
        {isSelected && (
          <span className="absolute top-1 right-1 bg-[#DC3558] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
            ✓
          </span>
        )}
      </div>

      {/* Name label — always visible below image */}
      <div
        className="px-2 py-1.5 flex items-center gap-1"
        style={{ background: "#fff", minHeight: compact ? 28 : 36 }}
      >
        <span className="text-[10px]">{getFlagEmoji(loc.country)}</span>
        <span
          className="text-xs font-semibold leading-tight truncate"
          style={{ color: "#1f2937" }}
        >
          {loc.name}
        </span>
      </div>
    </button>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0" style={{ zIndex: 9999 }}>
          {/* Backdrop */}
          <motion.div
            onClick={dismissAsAll}
            className="absolute inset-0"
            style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* ══════════ MOBILE BOTTOM SHEET ══════════ */}
          {isMobile ? (
            <motion.div
              className="fixed bottom-0 left-0 right-0"
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={sheetVariants}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                className="w-full rounded-t-2xl flex flex-col"
                style={{
                  background: "#fff",
                  maxHeight: "90vh",
                  boxShadow: "0 -8px 40px rgba(0,0,0,0.18)",
                }}
              >
                {/* Drag handle */}
                <div className="flex justify-center pt-2 pb-1 shrink-0">
                  <div className="w-10 h-1 rounded-full bg-gray-300" />
                </div>

                {/* Header */}
                <div
                  className="shrink-0 px-4 py-3 flex items-center justify-between"
                  style={{ borderBottom: "1px solid #f3f4f6" }}
                >
                  <div>
                    <h2 className="text-base font-bold" style={{ color: BRAND }}>
                      Select Your City
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5">Find events near you</p>
                  </div>
                  <button
                    onClick={dismissAsAll}
                    className="w-8 h-8 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors text-lg"
                  >
                    ✕
                  </button>
                </div>

                {/* Search bar */}
                <div className="shrink-0 px-4 py-3" style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
                    <input
                      ref={inputRef}
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      onKeyDown={onInputKeyDown}
                      placeholder="Search city (e.g. Berlin, Munich…)"
                      className="w-full pl-9 pr-10 py-2.5 text-sm rounded-xl focus:outline-none focus:ring-2 transition-all"
                      style={{
                        border: "1.5px solid #e5e7eb",
                        background: "#f9fafb",
                        color: "#111827",
                      }}
                      aria-autocomplete="list"
                      aria-controls="city-suggestions"
                    />
                    {query && (
                      <button
                        aria-label="Clear search"
                        onClick={() => { setQuery(""); setSuggestions([]); setSuggestionsOpen(false); }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-xs hover:bg-gray-300 transition-colors"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>

                {/* Scrollable body */}
                <div className="overflow-y-auto flex-1 pb-4">
                  {/* Recent chips */}
                  {recent.length > 0 && !query && (
                    <div className="px-4 pt-3 pb-2">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Recent</p>
                      <div className="flex gap-2 flex-wrap">
                        {recent.map((r) => (
                          <button
                            key={r}
                            onClick={() => selectSuggestion(r, { custom: true, recent: true })}
                            className="text-xs px-3 py-1.5 rounded-full font-medium transition-all hover:shadow-sm"
                            style={{
                              border: "1px solid #e5e7eb",
                              background: "#f9fafb",
                              color: "#374151",
                            }}
                          >
                            🕐 {r}
                          </button>
                        ))}
                        <button
                          onClick={() => { setRecent([]); localStorage.removeItem("city_recent"); }}
                          className="text-xs px-3 py-1.5 rounded-full text-red-500 hover:bg-red-50 transition-colors"
                          style={{ border: "1px solid #fee2e2" }}
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Mapbox suggestions dropdown */}
                  {(loadingSuggestions || suggestionsOpen) && (
                    <div className="px-4 pt-2 pb-1">
                      <div
                        className="rounded-xl overflow-hidden"
                        style={{ border: "1px solid #e5e7eb", background: "#fff", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
                      >
                        {loadingSuggestions ? (
                          <SkeletonRows />
                        ) : (
                          <ul id="city-suggestions" role="listbox">
                            {suggestions.map((s, idx) => (
                              <li
                                id={`suggestion-${idx}`}
                                key={s.id || s.display}
                                role="option"
                                aria-selected={highlightIndex === idx}
                                onMouseEnter={() => setHighlightIndex(idx)}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => selectSuggestion(s, { custom: false, source: "mapbox" })}
                                className="flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors"
                                style={{
                                  background: highlightIndex === idx ? "#eef2ff" : "transparent",
                                  borderBottom: idx < suggestions.length - 1 ? "1px solid #f3f4f6" : "none",
                                }}
                              >
                                <div
                                  className="w-8 h-8 rounded-full flex items-center justify-center text-sm shrink-0"
                                  style={{ background: "#f3f4f6" }}
                                >
                                  🏙️
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-semibold text-gray-800 truncate">
                                    {renderSuggestionLabel(s)}
                                  </div>
                                  {s.place_name && (
                                    <div className="text-xs text-gray-400 truncate">{s.place_name}</div>
                                  )}
                                </div>
                                <span className="text-gray-300 text-xs shrink-0">→</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="px-4 pt-3 pb-1 flex gap-2">
                    <button
                      onClick={() => handleCloseModal("All")}
                      className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all hover:shadow-md"
                      style={{
                        border: "1.5px solid #e5e7eb",
                        background: "#f9fafb",
                        color: "#374151",
                      }}
                    >
                      🌍 All Cities
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); detectLocation(); }}
                      className="py-2.5 px-4 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 hover:shadow-md"
                      style={{ background: BRAND }}
                    >
                      📍 Detect
                    </button>
                  </div>

                  {/* City grid — local locations */}
                  <div className="px-4 pt-3">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                      {query ? `Results for "${query}"` : "Popular Cities"}
                    </p>

                    {filtered.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2.5">
                        {filtered.map((loc) => (
                          <CityCard
                            key={loc.value}
                            loc={loc}
                            isSelected={locationLabel === loc.value}
                            compact
                            onClick={() => selectSuggestion(loc.value, { custom: false, source: "local" })}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="py-8 text-center">
                        <div className="text-3xl mb-2">🔍</div>
                        <p className="text-sm text-gray-500">No cities found for "{query}"</p>
                        <p className="text-xs text-gray-400 mt-1">Try the search above for any city worldwide</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>

          ) : (
            /* ══════════ DESKTOP CENTER MODAL ══════════ */
            <div className="fixed inset-0 flex items-center justify-center p-6">
              <motion.div
                onClick={(e) => e.stopPropagation()}
                className="w-full rounded-2xl overflow-hidden flex flex-col"
                style={{
                  maxWidth: 920,
                  height: "85vh",
                  background: "#fff",
                  boxShadow: "0 25px 60px rgba(0,0,0,0.2)",
                }}
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={modalVariants}
              >
                {/* Inner row: left panel + right panel, must fill remaining height */}
                <div className="flex flex-row flex-1 min-h-0">
                {/* Left panel */}
                <div
                  className="w-72 shrink-0 flex flex-col overflow-y-auto"
                  style={{ borderRight: "1px solid #f3f4f6", background: "#fafafa" }}
                >
                  <div className="p-5" style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <div className="flex items-start justify-between mb-1">
                      <div>
                        <h2 className="text-lg font-bold" style={{ color: BRAND }}>
                          Select City
                        </h2>
                        <p className="text-xs text-gray-400 mt-0.5">Find events near you</p>
                      </div>
                      <button
                        onClick={() => handleCloseModal("All")}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-200 transition-colors"
                      >
                        ✕
                      </button>
                    </div>

                    {/* Search */}
                    <div className="relative mt-3">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
                      <input
                        ref={inputRef}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={onInputKeyDown}
                        placeholder="Search any city…"
                        className="w-full pl-9 pr-3 py-2.5 text-sm rounded-xl focus:outline-none focus:ring-2 transition-all"
                        style={{
                          border: "1.5px solid #e5e7eb",
                          background: "#fff",
                          color: "#111827",
                        }}
                        aria-autocomplete="list"
                        aria-controls="city-suggestions-desktop"
                      />
                    </div>

                    {/* Mapbox suggestions */}
                    {(loadingSuggestions || suggestionsOpen) && (
                      <div
                        className="mt-2 rounded-xl overflow-hidden"
                        style={{ border: "1px solid #e5e7eb", background: "#fff", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
                      >
                        {loadingSuggestions ? (
                          <SkeletonRows />
                        ) : (
                          <ul id="city-suggestions-desktop" role="listbox" className="max-h-44 overflow-y-auto">
                            {suggestions.map((s, idx) => (
                              <li
                                key={s.id || s.display}
                                role="option"
                                aria-selected={highlightIndex === idx}
                                onMouseEnter={() => setHighlightIndex(idx)}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => selectSuggestion(s, { custom: false, source: "mapbox" })}
                                className="flex items-center gap-2 px-3 py-2.5 cursor-pointer transition-colors"
                                style={{
                                  background: highlightIndex === idx ? "#eef2ff" : "transparent",
                                  borderBottom: idx < suggestions.length - 1 ? "1px solid #f3f4f6" : "none",
                                }}
                              >
                                <span className="text-xs shrink-0">🏙️</span>
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-semibold text-gray-800 truncate">
                                    {renderSuggestionLabel(s)}
                                  </div>
                                  {s.place_name && (
                                    <div className="text-[10px] text-gray-400 truncate">{s.place_name}</div>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className="p-4 space-y-2">
                    <button
                      onClick={() => handleCloseModal("All")}
                      className="w-full py-2.5 rounded-xl text-sm font-semibold transition-all hover:shadow-md"
                      style={{ border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151" }}
                    >
                      🌍 All Cities (Global)
                    </button>
                    <button
                      onClick={detectLocation}
                      className="w-full py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
                      style={{ background: BRAND }}
                    >
                      📍 Detect My Location
                    </button>
                  </div>

                  {/* Recent searches */}
                  {recent.length > 0 && (
                    <div className="px-4 pb-4">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Recent</p>
                      <div className="flex flex-wrap gap-1.5">
                        {recent.map((r) => (
                          <button
                            key={r}
                            onClick={() => selectSuggestion(r, { custom: true, recent: true })}
                            className="text-xs px-2.5 py-1 rounded-full transition-all hover:shadow-sm"
                            style={{ border: "1px solid #e5e7eb", background: "#fff", color: "#374151" }}
                          >
                            {r}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Quick picks label */}
                  <div className="px-4">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Quick picks</p>
                    <div className="mt-2 flex flex-wrap gap-1.5 pb-4">
                      {locations.slice(0, 8).map((c) => (
                        <button
                          key={c.value}
                          onClick={() => handleCloseModal(c.value)}
                          className="text-xs px-2.5 py-1 rounded-full transition-all"
                          style={{
                            border: locationLabel === c.value ? "1.5px solid #DC3558" : "1px solid #e5e7eb",
                            background: locationLabel === c.value ? "#fff1f4" : "#fff",
                            color: locationLabel === c.value ? "#DC3558" : "#374151",
                            fontWeight: locationLabel === c.value ? 700 : 500,
                          }}
                        >
                          {getFlagEmoji(c.country)} {c.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right panel — city grid, scrollable */}
                <div className="flex-1 overflow-y-auto">
                  <div className="p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-bold text-gray-700">
                        {query
                          ? `Results for "${query}" (${filtered.length})`
                          : `All Cities (${locations.length})`}
                      </h3>
                    </div>

                    <div className="grid grid-cols-4 gap-3">
                      {/* All Cities tile */}
                      <button
                        onClick={() => handleCloseModal("All")}
                        className="rounded-xl flex flex-col items-center justify-center gap-2 pt-5 pb-4 transition-all hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        style={{
                          border: "1.5px dashed #c7d2fe",
                          background: "rgba(99,102,241,0.03)",
                          minHeight: 120,
                        }}
                      >
                        <div className="text-3xl">🌍</div>
                        <div className="text-sm font-bold" style={{ color: BRAND }}>
                          All Cities
                        </div>
                        <div className="text-[10px] text-gray-400">Global</div>
                      </button>

                      {filtered.map((loc) => (
                        <CityCard
                          key={loc.value}
                          loc={loc}
                          isSelected={locationLabel === loc.value}
                          onClick={() => handleCloseModal(loc.value)}
                        />
                      ))}
                    </div>

                    {filtered.length === 0 && (
                      <div className="col-span-4 py-16 text-center">
                        <div className="text-4xl mb-3">🔍</div>
                        <p className="text-sm font-semibold text-gray-600">No cities found</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Try searching for any city in the search bar
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                {/* end flex-row inner row */}
                </div>
              </motion.div>
            </div>
          )}
        </div>
      )}
    </AnimatePresence>
  );
}
